/**
 * The renderer tier this device starts with: `?tier=off|medium` overrides;
 * else the tier the player chose, remembered in `localStorage` (there is no
 * chooser yet: `rememberTier` is what it will call); else the renderer's
 * capability check (`defaultTier`: Medium with WebGL2, half-float colour
 * buffers and depth textures, else Off).
 */
import { type QualityTier, defaultTier, qualityTiers } from '@ab/renderer';

const storageKey = 'ab.tier';

export const isTier = (value: string | null): value is QualityTier => value !== null && (qualityTiers as readonly string[]).includes(value);

export const chooseTier = (params: URLSearchParams): QualityTier => {
  const asked = params.get('tier');
  if (isTier(asked)) return asked;
  try {
    const saved = localStorage.getItem(storageKey);
    if (isTier(saved)) return saved;
  } catch {
    // Storage can be unavailable (a private window); the capability check decides then.
  }
  return defaultTier(document);
};

/** Remembers a tier the player chose (never one the capability check picked). */
export const rememberTier = (tier: QualityTier): void => {
  try {
    localStorage.setItem(storageKey, tier);
  } catch {
    // Not remembered; the next visit asks the capability check again.
  }
};
